import moment from 'moment';

import {
  FLOW_METRICS_INTERVAL_LAST_24H,
  FLOW_METRICS_INTERVAL_TODAY,
  FLOW_METRICS_INTERVAL_LAST_7_DAYS,
  FLOW_METRICS_INTERVAL_LAST_15_DAYS,
} from 'js/constants/metricsConstants';

/**
 * A `{ from, to }` window passed straight to the metrics endpoint's `from`/`to` query params.
 *
 * Two encodings, both accepted by the backend (Joda parses each as a UTC instant):
 *  - `last24h`  -> ISO-8601 instants (`...Z`)  — a rolling, timezone-independent 24h window.
 *  - calendar windows (`today`/`last7Days`/`last15Days`) -> date-only `YYYY-MM-DD` (UTC days).
 */
export type MetricsWindowRange = { from: string; to: string };

const DAY_FORMAT = 'YYYY-MM-DD';

// Number of calendar days each calendar-day window spans, counting today.
// The value matches the label number: "Last 7 days" => 7 days ending today.
const WINDOW_INTERVAL_DAYS: Record<string, number> = {
  [FLOW_METRICS_INTERVAL_TODAY]: 1,
  [FLOW_METRICS_INTERVAL_LAST_7_DAYS]: 7,
  [FLOW_METRICS_INTERVAL_LAST_15_DAYS]: 15,
};

// Hours in the rolling window. Kept as a constant so a future "last 48h" etc. is a one-liner.
const LAST_24H_HOURS = 24;

/**
 * Turns a window interval into a `{ from, to }` pair for the metrics endpoint, or `null` for
 * non-window intervals (lifetime / latestRun / custom / unknown).
 *
 * ## `last24h` — rolling, timezone-independent (the window the control surfaces today)
 *
 * `{ from: now − 24h, to: now }` as ISO instants. Both ends are absolute moments, so the
 * window means the exact same 24 hours for every user regardless of their timezone — there is
 * no calendar "day" to translate and no day-boundary edge case. The backend resolves this
 * sub-day range against its hourly buckets (`data_*_hourly`), which are retained ~7 days, so a
 * rolling 24h is always representable. The current partial hour is included (the backend floors
 * `to` to the hour and matches inclusively).
 *
 * This deliberately differs from the calendar-day windows below and from the Flow Insights
 * date picker, which model *calendar days* in the user's local timezone (see
 * `FlowInsights/utils/normalizeDateRange.ts`). Those need local↔UTC day translation precisely
 * because a "day" is timezone-relative; a rolling 24h does not.
 *
 * ## calendar-day windows — `today` / `last7Days` / `last15Days` (defined, not surfaced)
 *
 * Inclusive `{ from, to }` calendar-day pair (date-only `YYYY-MM-DD`, UTC), covering
 * `WINDOW_INTERVAL_DAYS[interval]` days ending today, so `to.diff(from,'days') + 1` equals that
 * count. These hit the backend's daily buckets (`data_*_daily`, retained indefinitely):
 *  - today      -> { from: today,       to: today }  (1 day)
 *  - last7Days  -> { from: today - 6d,  to: today }  (7 days)
 *  - last15Days -> { from: today - 14d, to: today }  (15 days)
 * They are kept for the custom-range follow-up / re-surfacing; the control does not show them.
 *
 * Each branch derives both ends from a single `moment.utc()` anchor so tests can assert the
 * delta between them.
 */
export function resolveWindowRange(interval: string): MetricsWindowRange | null {
  if (interval === FLOW_METRICS_INTERVAL_LAST_24H) {
    const now = moment.utc();
    const from = now.clone().subtract(LAST_24H_HOURS, 'h');

    return {
      from: from.toISOString(),
      to: now.toISOString(),
    };
  }

  const days = WINDOW_INTERVAL_DAYS[interval];
  if (days == null) return null;

  const today = moment.utc();
  const from = today.clone().subtract(days - 1, 'd');

  return {
    from: from.format(DAY_FORMAT),
    to: today.format(DAY_FORMAT),
  };
}
